import { findMoveById } from '@/data/moves';
import { findSpellsByClass, findSpellsByClassAndTradition } from '@/data/spells';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { getActiveSpellLists, isSpellAccessAvailable } from '@/services/spellAccess';
import { inferEquipmentSelectionsFromInventory } from '../equipmentSelections';
import { isMoveSetupComplete } from './moveSetupComplete';
import type { BuilderSectionId, CharacterDraft } from '../types';

/**
 * Hint user-facing che descrive cosa manca per completare una sezione del
 * builder. Pattern equivalente a `missingHintFor` del mockup
 * `app.jsx:98-180`, replicato sui predicati DWR di `isSectionComplete`.
 *
 * - Ritorna `null` se la sezione è già completa o se non è applicabile (es.
 *   `archetype` senza classe selezionata, `spells` per PG senza casting).
 * - Stringhe in italiano, registro PM-friendly. Pluralizzazione esplicita.
 *
 * Pattern SSOT: un solo hint per sezione, sempre derivato dallo state del
 * draft. Il consumer (`SectionList` in BuilderPage) lo passa al
 * `<SectionCompleteBadge>` per mostrare il banner accent-dwr quando
 * `done=false`.
 */
export function getSectionMissingHint(
  section: BuilderSectionId,
  draft: CharacterDraft,
  classDef: ClassDefinition | undefined,
): string | null {
  return HINT_BY_SECTION[section](draft, classDef);
}

type HintFn = (draft: CharacterDraft, classDef: ClassDefinition | undefined) => string | null;

const HINT_BY_SECTION: Record<BuilderSectionId, HintFn> = {
  class: (draft) => (draft.classId ? null : 'Seleziona una classe.'),
  race: (draft) => (draft.race ? null : 'Scegli una razza o specifica una razza custom.'),
  archetype: (draft) => (draft.archetypeId ? null : 'Scegli un archetipo.'),
  stats: (draft) => (draft.stats ? null : 'Assegna i punteggi alle sei caratteristiche.'),
  alignment: (draft) => {
    if ((draft.alignment?.precept.length ?? 0) > 0) return null;
    if (!draft.alignment) return 'Scegli un allineamento e compila il precetto.';
    return 'Compila il precetto del tuo allineamento.';
  },
  aspetti: (draft, classDef) => {
    if (!classDef) return null;
    const chosenCategorie = new Set(draft.chosenAspetti.map((a) => a.categoria));
    const missing = classDef.aspetti
      .filter((a) => !chosenCategorie.has(a.label))
      .map((a) => a.label);
    return missing.length === 0 ? null : `Manca: ${missing.join(', ')}.`;
  },
  equipment: (draft, classDef) => {
    if (!classDef) return null;
    const selections = inferEquipmentSelectionsFromInventory(
      draft.inventory,
      classDef.startingEquipment.choices,
    );
    const incompleteGroups = classDef.startingEquipment.choices.filter((group) => {
      const matched = selections[group.id] ?? [];
      return matched.length < group.pickCount;
    });
    if (incompleteGroups.length > 0) {
      const n = incompleteGroups.length;
      return `Manca${n === 1 ? 'no' : ''} ${n} scelt${n === 1 ? 'a' : 'e'} di equipaggiamento.`;
    }
    // **FI-50**: item con `userDescriptionPrompt` non compilati.
    const missingDescriptions = draft.inventory.filter(
      (item) => item.userDescriptionPrompt && (item.userDescription?.trim().length ?? 0) === 0,
    );
    if (missingDescriptions.length === 0) return null;
    const names = missingDescriptions.map((item) => `«${item.name}»`).join(', ');
    return `Descrivi: ${names}.`;
  },
  'starting-moves': (draft, classDef) => {
    if (!classDef) return null;
    const expected =
      classDef.startingMoves.length +
      (draft.archetypeId ? 1 : 0) +
      classDef.startingChoiceGroups.reduce((s, g) => s + g.pickCount, 0);
    const have = draft.moves.length;
    if (have < expected) {
      const delta = expected - have;
      return delta === 1
        ? `Scegli ancora 1 mossa iniziale.`
        : `Scegli ancora ${delta} mosse iniziali.`;
    }
    // Mosse complete: verifica setup interattivo (choices / choiceGroups /
    // narrativeFields). Le mosse senza setup ritornano sempre true.
    const incompleteSetup = draft.moves.filter((moveRef) => {
      const move = findMoveById(classDef.id, moveRef.id);
      if (!move) return false;
      return !isMoveSetupComplete(move, moveRef, draft.archetypeId);
    });
    if (incompleteSetup.length === 0) return null;
    const names = incompleteSetup
      .map((ref) => findMoveById(classDef.id, ref.id)?.name ?? ref.id)
      .join(', ');
    return `Completa le scelte di: ${names}.`;
  },
  spells: (draft, classDef) => spellsHint(draft, classDef),
  'identity-bonds': (draft) => {
    const reasons: string[] = [];
    if (!draft.name?.length) reasons.push('il nome del personaggio');
    if (!draft.bondsDeferred && draft.bonds.length < 1) {
      reasons.push('almeno un legame (oppure rinvialo alla prima sessione)');
    }
    return reasons.length === 0 ? null : `Manca: ${reasons.join(' e ')}.`;
  },
};

function spellsHint(draft: CharacterDraft, classDef: ClassDefinition | undefined): string | null {
  if (!classDef?.spellcasting) return null;
  const lists = getActiveSpellLists(draft.moves);
  if (lists.length === 0) return null; // PG senza casting attivo → sezione non bloccante
  const have = draft.spells.length;
  const expectedTotal = lists.reduce((sum, list) => sum + expectedSpellsForList(list, draft), 0);
  if (have >= expectedTotal) return null;
  const delta = expectedTotal - have;
  return `Scegli ancora ${delta} incantesim${delta === 1 ? 'o' : 'i'}.`;
}

function expectedSpellsForList(
  list: ReturnType<typeof getActiveSpellLists>[number],
  draft: CharacterDraft,
): number {
  const ownedMoveIds = draft.moves.map((m) => m.id);
  if (list.mode === 'mago-native') return 6; // 3 trucchetti + 3 spell L1
  if (list.mode === 'warlock-native') {
    const cantrips = findSpellsByClass('warlock').filter((s) => s.level === 0).length;
    return cantrips + 2;
  }
  if (list.mode === 'druido-native') {
    // PR-A2: applichiamo `isSpellAccessAvailable` anche al Druido perché il
    // nuovo `getSpellPoolForGrantInContext` filtra `requiresMove`
    // (es. `chierico.curare-ferite-moderate` richiede `chierico.primo-soccorso-migliorato`).
    return findSpellsByClass('druido')
      .filter((s) => s.level === 0 || s.level === 1)
      .filter((s) => {
        const access = s.availableTo.find((a) => a.classId === 'druido');
        if (!access) return true;
        return isSpellAccessAvailable(access, { archetypeId: draft.archetypeId, ownedMoveIds });
      }).length;
  }
  // chierico-native / paladino-native: spell della Tradizione filtrati per
  // requiresArchetype + requiresMove via isSpellAccessAvailable.
  const classId = list.mode === 'chierico-native' ? 'chierico' : 'paladino';
  return findSpellsByClassAndTradition(classId, list.tradizione)
    .filter((s) => s.level === 0 || s.level === 1)
    .filter((s) => {
      const access = s.availableTo.find(
        (a) =>
          a.classId === classId && (a as { tradizione?: string }).tradizione === list.tradizione,
      );
      if (!access) return true;
      return isSpellAccessAvailable(access, { archetypeId: draft.archetypeId, ownedMoveIds });
    }).length;
}
